import React, { ReactElement, useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { Button, Input } from '@appsumo/dorado-react';

import { useCart } from '~/lib/cart';
import { currencyWithCents } from '~/lib/format';
import { CHECKOUT_ERRORS } from '~/constants/checkout';
import { SimpleButtonTypes } from '~/constants/global';
import { CartContextProps } from '~/types/cart';

import SimpleButton from '~/components/common/SimpleButton';
import { Loader } from '~/components/ui';

enum CouponStatus {
  NO_COUPON = 'no-coupon',
  PENDING_INPUT = 'pending-input',
  COUPON_APPLIED = 'coupon-applied',
}

export default function Discounts({
  couponError,
  isCouponLoading,
  isDisabled,
  setCouponError,
  cart,
}: Readonly<{
  couponError: typeof CHECKOUT_ERRORS.COUPON;
  isCouponLoading: boolean;
  isDisabled: boolean;
  setCouponError: (error: typeof CHECKOUT_ERRORS.COUPON) => void;
  cart: CartContextProps['cart'];
}>): ReactElement {
  const { applyCoupon, unapplyCoupon } = useCart();
  const [couponStatus, setCouponStatus] = useState<CouponStatus>(
    CouponStatus.NO_COUPON,
  );
  const [couponCode, setCouponCode] = useState('');

  const resetCouponInput = useCallback(() => {
    setCouponStatus(CouponStatus.NO_COUPON);
    setCouponCode('');
    setCouponError('');
  }, [setCouponError]);

  useEffect(() => {
    if (cart?.coupon?.coupon_id) {
      setCouponStatus(CouponStatus.COUPON_APPLIED);
      setCouponCode(cart.coupon.code); // let's ensure client state in sync w/ server
    } else {
      resetCouponInput();
    }
  }, [cart?.coupon?.coupon_id, cart?.coupon?.code, resetCouponInput]);

  const handleCouponCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCouponError('');
    setCouponCode(e.target.value);
  };

  const updateCouponStatus = async (): Promise<void> => {
    const err =
      couponStatus === CouponStatus.PENDING_INPUT
        ? await applyCoupon(couponCode)
        : await unapplyCoupon(couponCode);

    setCouponError(err);
  };
  const buttonDisabled = !couponCode || isCouponLoading || isDisabled;
  const buttonClass = clsx('flex self-start', {
    '!border-gravel': buttonDisabled,
  });

  return (
    <div>
      <div>Coupon savings</div>
      {couponStatus === CouponStatus.NO_COUPON && (
        <div className="mt-2">
          <SimpleButton
            type={SimpleButtonTypes.applyCoupon}
            data-testid="discount-checkout-toggle-discount"
            onClick={() => {
              setCouponStatus(CouponStatus.PENDING_INPUT);
            }}
          />
        </div>
      )}
      {couponStatus === CouponStatus.PENDING_INPUT && (
        <div className="mt-2 flex w-full items-center">
          <div className="mr-4 grow">
            <Input
              name="discount-code"
              data-testid="discount-checkout-discount-input"
              type="text"
              placeholder="Enter coupon code"
              error={couponError}
              onChange={handleCouponCodeChange}
            />
            <button className="mt-2 text-brick" onClick={resetCouponInput}>
              Cancel
            </button>
          </div>
          <Button
            className={buttonClass}
            secondary
            onClick={updateCouponStatus}
            disabled={buttonDisabled}
            data-testid="discount-checkout-apply-discount"
          >
            <span>Apply</span>
            {isCouponLoading && (
              <span className="ml-2">
                <Loader size="sm" />
              </span>
            )}
          </Button>
        </div>
      )}
      {couponStatus === CouponStatus.COUPON_APPLIED && !!cart?.coupon_desc && (
        <div>
          <div className="mt-2 flex italic text-grace">
            {/* todo: we might need update/create new method for coupon_desc to match figma coupon copy */}
            <div className="pr-1" data-testid="discount-checkout-coupon-desc">
              {cart.coupon_desc}
            </div>
            <div
              className="ml-auto whitespace-nowrap font-medium"
              data-testid="discount-checkout-coupon-discount"
            >{`-${currencyWithCents(cart.coupon_discount)}`}</div>
          </div>
          <div
            className="inline-flex cursor-pointer"
            onClick={updateCouponStatus}
            onKeyDown={updateCouponStatus}
            data-testid="discount-checkout-remove-button"
            role="button"
            tabIndex={0}
          >
            <SimpleButton type={SimpleButtonTypes.remove} />
            <span
              className="mr-2 italic text-brick"
              data-testid="discount-checkout-coupon-code"
            >
              &quot;{cart.coupon.code}&quot;
            </span>
            {isCouponLoading && <Loader size="sm" />}
          </div>
        </div>
      )}
      {!!cart?.plan_discount_type && !!cart?.plan_discount && (
        <div className="mt-2 flex">
          <div
            className="pr-1"
            data-testid="discount-checkout-plan-discount-type"
          >
            {cart.plan_discount_type} member savings
          </div>
          <div
            className="ml-auto whitespace-nowrap font-medium"
            data-testid="discount-checkout-plan-discount"
          >
            -{currencyWithCents(cart.plan_discount)}
          </div>
        </div>
      )}
    </div>
  );
}
